import { VALID_PAYMENT_METHODS, VALID_UPI_PAYMENT_CODE, PAYMENT_CODES } from "@swiggy-private/payment-core";
import { NEWRELIC_TABLES, getDownstreamHostsValue } from "@swiggy-private/checkout-core";

export const PLACE_ORDER_EXTRA_PARAMS = {
    SWIGGY_PAY_AMOUNT: "swiggy_pay_amount",
};

export const SUPER_ORDER_TAG = "SUPER";
export const PRIORITY_DELIVERY_TAG = "PRIORITY";
export const CASH = "Cash";

export enum ORDER_STATUS {
    SUCCESS = "success",
    FAILED = "failed",
}

export type PLACE_ORDER_EVENT_TYPE =
    | typeof NEWRELIC_TABLES.PAYMENTS_WEB_PLACE_ORDER
    | typeof NEWRELIC_TABLES.PAYMENTS_WEB_DIRECT_PLACE_ORDER
    | typeof NEWRELIC_TABLES.PAYMENTS_WEB_DIRECT_PLACE_ORDER_FAILURE;

export const ALLOWED_METHODS_FOR_PENDING_PAYMENT = [
    PAYMENT_CODES.JUSPAY,
    PAYMENT_CODES.NETBANKING,
    PAYMENT_CODES.UPICOLLECT,
    PAYMENT_CODES.UPIINTENT,
    PAYMENT_CODES.AMAZONPAYWEB,
    PAYMENT_CODES.AMAZONPAY,
    PAYMENT_CODES.PHONEPE,
    PAYMENT_CODES.CRED,
    PAYMENT_CODES.PHONEPEUPI,
    PAYMENT_CODES.INAPPUPI,
];

export const ALLOWED_METHODS_APP_STATE_IN_IDLE_MODE = [
    VALID_PAYMENT_METHODS.CRED,
    VALID_UPI_PAYMENT_CODE.UPI_COLLECT,
    // VALID_UPI_PAYMENT_CODE.UPI_INTENT, Disable it for now, bcz polling happens in bg for upi intent for ios and it breaks
];

export const OFFER_TAG_TITLE = "Free delivery with offer pack";
export const OFFER_TAG_MILESTONE_TITLE = "Reward Unlocked";

export const PreBook = "Pre-Book";
export const PrimeSlot = "PrimeSlot";
export const GirfPayment = "GIRF_PAYMENT";
export const Events = "EVENTS";
export const PreebookPrepaid = "Prebook-Prepaid";

const getCbccRedirectUrl = () => {
    const baseUrl =
        getDownstreamHostsValue("CBCC_REDIRECT_URL") ??
        "https://webviews.swiggy.com/orange-card/application_start_direct/";
    return `swiggy://webviewV2?webview_url=${encodeURIComponent(baseUrl)}`;
};

export const CBCC_REDIRECT_URL = getCbccRedirectUrl();

export enum ORDER_CONTEXT {
    EDIT = "edit",
    POST_ORDER_TIPPING = "post_order_tipping",
    SWIGGY_SUPER = "swiggy_super",
    PAY_ON_DELIVERY = "pay_on_delivery",
    SWIGGY_ONE = "subscription",
}

export enum ORDER_TYPE {
    SUBSCRIPTION = "subscription",
}

export const DEFAULT_CONFIRM_ORDER_STATUS_LIST = [
    "CONFIRMED",
    "DELIVERY_ASSIGNED",
    "DELIVERY_CONFIRMED",
    "DELIVERY_UNASSIGNED",
    "DELIVERY_ARRIVED",
    "DELIVERY_PICKEDUP",
    "DELIVERY_REACHED",
    "DELIVERY_DELIVERED",
];

/*
 * These parameters are being used for payment pending flow in case of Food, Dash, edit-order.
 * The payment pending params are passed to Native through NB call at time of place-order,
 * this data is used by native at time of app launch to confirm the order in case not already done.
 * Below versions include app support for Webpayments confirm order call from app side,
 * which is why a version check is required here.
 *
 */
export const APP_LAUNCH_CONFIRM_ORDER_URI = "api/v1/order/confirm-on-app-launch";

export const DEFAULT_MIN_ANDROID_VERSION_FOR_PAYMENT_PENDING_FLOW = 1034;
export const DEFAULT_MIN_IOS_VERSION_FOR_PAYMENT_PENDING_FLOW = 420;

export const DEFAULT_MIN_ANDROID_VERSION_FOR_PAYMENT_PENDING_FLOW_PUBLIC_API = 1515;
export const DEFAULT_MIN_IOS_VERSION_FOR_PAYMENT_PENDING_FLOW_PUBLIC_API = 665;

export const V3_ENDPOINTS = {
    POST_ORDER_URI: "/api/v3/order/place",
    CONFIRM_ORDER_URI: "/api/v3/order/confirm",
    CONFIRM_TRANSACTION_URI: "/api/v3/payment/confirm",
    PLACE_EDIT_ORDER_URI: "/api/v3/order/edit/place",
    CONFIRM_EDIT_ORDER_URI: "/api/v3/order/edit/confirm",
    DASH_PLACE_ORDER_URI: "/api/v3/checkout/order",
    NEW_CONFIRM_DASH_ORDER: "/api/v3/checkout/confirm/order",
    NEW_CONFIRM_ASSURE_ORDER: "/api/v3/checkout/assure/confirm/order",
    NEW_CONFIRM_JUST_ORDER: "/api/v3/checkout/just/confirm/order",
    SWIGGY_MONEY_ORDER_URI: "/api/v3/payment/add-money/order",
    INSTAMART_ORDER_DETAILS_URI: "/api/v3/instamart/order/{0}/context/ORDER_JOB",
    DASH_ORDER_DETAILS_URI: "/api/v3/dash/order/{0}/context/ORDER_JOB",
    DINEOUT_ORDER_DETAILS_URI: "/api/v3/dineout/order/{0}/context/ORDER_JOB",
    ASSURE_ORDER_DETAILS_URI: "/api/v3/assure/order/{0}/context/ORDER_JOB",
    JUST_ORDER_DETAILS_URI: "/api/v3/just/order/{0}/context/ORDER_JOB",
    SUPER_CONFIRM_ORDER_URI: "/api/v3/marketplace/order/confirm",
    DINEOUT_PLACE_ORDER_URI: "/api/v3/dineout/checkout/order",
    DINEOUT_CONFIRM_ORDER_URI: "/api/v3/dineout/checkout/confirm/order",
    INSTAMART_PLACE_ORDER_URI: "/api/v3/instamart/checkout/order",
    ASSURE_PLACE_ORDER_URI: "/api/v3/assure/checkout/order",
    JUST_PLACE_ORDER_URI: "/api/v3/just/checkout/order",
    DASH_PLACE_ORDER_CUSTOM_URI: "/api/v3/custom/checkout/order",
    SWIGGY_SUPER_PLACE_ORDER_URI: "/api/v3/marketplace/order/place",
    CONFIRM_DASH_POD_URI: "/api/v3/confirmPayment",
};

export const CBCC_FD_PLACE_ORDER_ENDPOINT = "/v1/payments/cbcc/onboarding/payment";

export const REDIRECT_TO_MARKETPLACE = "shouldRedirectToMarketplace".toLowerCase();

export const DEFAULT_RETRY_RESPONSE = {
    statusCode: 5,
    statusMessage: "Error while debiting amount/Payment Failed",
    data: {
        orderData: null,
        retryCount: 1,
    },
};

export const PUBLIC_API_LOG_TABLES = {
    DASH_TRANSACTION_ERROR: "payment_web_api_dash_transaction_error",
    DASH_ORDER_DETAILS_ERROR: "payment_web_api_dash_order_details_error",
    DASH_ORDER_STATUS: "payment_web_api_dash_order_status",
    DINEOUT_NEW_CONFIRM_ORDER_STATUS: "payment_web_api_dineout_new_confirm_order_status",
};
